"use client";

import { MinusIcon, PlusIcon } from "lucide-react";
import Image from "next/image";
import { Progress } from "@/components/ui/progress";
import type { CartGetResult } from "@/dao/cart";
import { useLocale, useTranslations } from "@/i18n/client";
import { formatMoney } from "@/lib/currencies";
import { calculateCartSubtotal, formatPriceWithTax } from "@/lib/financial/money";
import { formatProductName, getVariantImages } from "@/lib/utils";
import { useCart, useChangeQuantity } from "@/ui/hooks/use-cart";
import { Button } from "@/ui/shadcn/button";
import { RequirePKDVerificationClient } from "@/ui/storefront/require-pkd-verification.client";
import { YnsLink } from "@/ui/yns-link";
import { CartAsideDrawer } from "./cart-aside-drawer";

export function CartModal({
	taxBehavior,
	currency,
	hidePrice,
	freeShippingThreshold,
}: {
	taxBehavior: "inclusive" | "exclusive";
	currency: string;
	hidePrice: boolean;
	freeShippingThreshold?: bigint | null;
}) {
	const { cart } = useCart();
	const locale = useLocale();
	const t = useTranslations("/cart.modal");

	if (!cart || cart.lineItems.length === 0) {
		return null;
	}

	const progress = freeShippingThreshold
		? (calculateCartSubtotal(cart, taxBehavior).toBigInt() * 100n) / freeShippingThreshold
		: null;
	const remainingForFreeShipping = freeShippingThreshold
		? freeShippingThreshold - calculateCartSubtotal(cart, taxBehavior).toBigInt()
		: null;

	return (
		<CartAsideDrawer>
			<div className="flex h-full min-h-[80vh] flex-col">
				<div className="px-4 py-6 sm:px-6">
					<div className="flex items-center justify-between">
						<h2 className="text-lg font-semibold text-neutral-700">{t("title")}</h2>
					</div>
				</div>

				{freeShippingThreshold &&
				progress !== null &&
				remainingForFreeShipping &&
				remainingForFreeShipping > 0 ? (
					<div className="px-6 py-4 transition-all border-y">
						<p className="text-sm text-slate-700 mb-3">
							{t("remainingForFreeShipping", {
								amount: formatMoney({
									amount: remainingForFreeShipping,
									currency: currency,
									locale,
								}),
							})}
						</p>

						<div className="relative">
							<Progress value={Number(progress)} className="h-2 bg-neutral-300" />
							<div
								className="absolute top-0 h-2 w-0.5 bg-neutral-800"
								style={{ left: `${Math.min(100, Number(progress))}%` }}
							/>
						</div>

						<div className="flex justify-between text-xs text-neutral-500 mt-1">
							<span>${0}</span>
							<span className="font-medium">${100}</span>
						</div>
					</div>
				) : null}

				<div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
					<RequirePKDVerificationClient hidePrice={hidePrice}>
						<div className="">
							<ul role="list" className="-my-6 divide-y divide-neutral-200">
								{cart.lineItems.map((line) => (
									<CartModalLineItem
										key={line.id}
										line={line}
										currency={currency}
										locale={locale}
										taxBehavior={taxBehavior}
									/>
								))}
							</ul>
						</div>
					</RequirePKDVerificationClient>
				</div>
				<div className="border-t border-neutral-200 px-4 py-6 sm:px-6">
					<RequirePKDVerificationClient hidePrice={hidePrice}>
						<div
							id="cart-overlay-description"
							className="flex justify-between text-lg font-semibold text-neutral-900"
						>
							<p>{t("total")}</p>
							<p>
								{formatMoney({
									amount: calculateCartSubtotal(cart, taxBehavior).toBigInt(),
									currency,
									locale,
								})}
							</p>
						</div>
						<p className="mt-0.5 text-sm text-neutral-500">{t("shippingAndTaxesInfo")}</p>
						<Button asChild={true} size={"lg"} className="mt-6 w-full rounded-full text-lg">
							<YnsLink href="/cart">{t("goToPaymentButton")}</YnsLink>
						</Button>
					</RequirePKDVerificationClient>
				</div>
			</div>
		</CartAsideDrawer>
	);
}

const CartModalLineItem = ({
	line,
	currency,
	locale,
	taxBehavior,
}: {
	line: CartGetResult["lineItems"][number];
	currency: string;
	locale: string;
	taxBehavior: "inclusive" | "exclusive";
}) => {
	const { formAction } = useChangeQuantity(line.quantity);
	const lineId = line.id;
	const { dispatch } = useCart();

	const images = getVariantImages(line.productVariant);

	return (
		<li
			key={line.id}
			className="grid grid-cols-[6rem_1fr_max-content] grid-rows-[auto_auto] gap-x-4 gap-y-2 py-6"
		>
			{images[0] ? (
				<div className="rounded-lg col-span-1 row-span-2 bg-neutral-100">
					<Image className="aspect-square object-cover" src={images[0]} width={120} height={120} alt="" />
				</div>
			) : (
				<div className="col-span-1 row-span-2" />
			)}

			<div className="">
				<h3 className="font-semibold leading-tight">{line.productVariant.product.name}</h3>
				<p className="font-medium text-sm text-muted-foreground">
					{formatProductName("", line.productVariant)}
				</p>
			</div>
			<p className="text-sm font-semibold leading-none w-full">
				{formatPriceWithTax({
					price: line.productVariant.price,
					currency: currency,
					locale: locale,
					taxRate: line.productVariant.product.productTaxRate?.taxRate.rate || 0n,
					taxBehavior: taxBehavior,
				})}
			</p>
			<div className="self-end text-sm font-medium">
				<div className="mt-auto flex items-center justify-between">
					<form className="flex items-center border rounded-full overflow-hidden bg-background">
						<Button
							variant="ghost"
							size="icon"
							className="h-8 w-8 rounded-none"
							disabled={line.quantity <= 0}
							type="submit"
							formAction={() => {
								dispatch({ type: "DECREASE", lineId });
								return formAction({ type: "DECREASE", lineId });
							}}
						>
							<MinusIcon className="h-3 w-3" />
							<span className="sr-only">Decrease quantity</span>
						</Button>
						<span className="w-8 text-center font-medium">{line.quantity}</span>
						<Button
							variant="ghost"
							size="icon"
							className="h-8 w-8 rounded-none"
							type="submit"
							formAction={() => {
								dispatch({ type: "INCREASE", lineId });
								return formAction({ type: "INCREASE", lineId });
							}}
						>
							<PlusIcon className="h-3 w-3" />
							<span className="sr-only">Increase quantity</span>
						</Button>
					</form>
				</div>
			</div>
		</li>
	);
};
